'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface QuickAccessTile {
  id: number;
  title: string;
  description: string;
  icon: string;
  link: string;
  color: string;
  stats: string;
}

const quickAccessTiles: QuickAccessTile[] = [
  {
    id: 1,
    title: 'Product Trading Portal',
    description: 'Browse 6000+ pharmaceutical products with complete regulatory documentation',
    icon: 'CubeIcon',
    link: '/product-trading-portal',
    color: 'bg-primary',
    stats: '6000+ Products',
  },
  {
    id: 2,
    title: 'Regulatory Consultancy',
    description: 'Expert guidance for market entry, compliance, and regulatory approvals',
    icon: 'DocumentCheckIcon',
    link: '/regulatory-consultancy-services',
    color: 'bg-secondary',
    stats: '60+ Countries',
  },
  {
    id: 3,
    title: 'Global Presence',
    description: 'Explore our international network and regional partnerships worldwide',
    icon: 'GlobeAltIcon',
    link: '/global-presence',
    color: 'bg-accent',
    stats: '5 Continents',
  },
];

export default function QuickAccessTiles() {
  const [visibleTiles, setVisibleTiles] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          quickAccessTiles.forEach((tile, index) => {
            setTimeout(() => {
              setVisibleTiles((prev) => [...prev, tile.id]);
            }, index * 150);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {quickAccessTiles.map((tile, index) => (
            <Link
              key={tile.id}
              href={tile.link}
              className={`group relative bg-card rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] border border-border overflow-hidden transform ${
                visibleTiles.includes(tile.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Background Gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 ${tile.color} opacity-5 rounded-full blur-3xl group-hover:opacity-20 group-hover:scale-150 transition-all duration-500`}></div>

              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-16 h-16 ${tile.color} rounded-xl mb-6 group-hover:scale-125 group-hover:rotate-6 transition-all duration-500 shadow-lg group-hover:shadow-xl`}>
                <Icon name={tile.icon as any} size={32} className="text-white" />
              </div>

              {/* Content */}
              <h3 className="font-heading font-semibold text-xl text-textPrimary mb-3 group-hover:text-primary transition-colors duration-300">
                {tile.title}
              </h3>
              <p className="text-sm text-textSecondary leading-relaxed mb-4 group-hover:text-textPrimary transition-colors duration-300">
                {tile.description}
              </p>

              {/* Stats Badge */}
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full group-hover:bg-primary/10 transition-all duration-300">
                <Icon name="ChartBarIcon" size={16} className="text-primary" />
                <span className="text-xs font-medium text-textPrimary">{tile.stats}</span>
              </div>

              {/* Arrow Icon */}
              <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300">
                <Icon name="ArrowRightIcon" size={24} className="text-primary" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}